import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Nav } from '../components/Nav';
import { Footer } from '../components/Footer';
import { CheckSquare, Users, Clock, Bell, BarChart, ListTodo, MessageSquare, Database, AlertTriangle, HelpCircle, UserX, CheckCircle2, XCircle, ArrowRight, Plug, Gift, Sparkles, Bot, Inbox, Workflow, PieChart, Calendar, ShieldCheck, Network, Repeat, Eye, Layout } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const painPoints = [{
  icon: <UserX size={28} data-id="element-1981" />,
  title: 'Managing the Manager',
  desc: 'You hired a project manager but still have to check if THEY did their job. You spend more time managing them than actually growing your business.'
}, {
  icon: <HelpCircle size={28} data-id="element-1982" />,
  title: 'Tasks Just... Disappear',
  desc: "Tasks get created, assigned, and then nobody follows up. Things just fall through the cracks and you don't find out until it's too late."
}, {
  icon: <AlertTriangle size={28} data-id="element-1983" />,
  title: 'Client Bottlenecks',
  desc: 'Your client was supposed to send content 3 days ago and nobody reminded them. Now the whole project is delayed.'
}, {
  icon: <Clock size={28} data-id="element-1984" />,
  title: 'Constant Chasing',
  desc: "You're exhausted from constantly asking 'Did this get done?' and 'Where are we on this?' You're the bottleneck for everything."
}];
const pmLogos = [{
  name: 'ClickUp',
  domain: 'clickup.com'
}, {
  name: 'Basecamp',
  domain: 'basecamp.com'
}, {
  name: 'Teamwork',
  domain: 'teamwork.com'
}, {
  name: 'Monday.com',
  domain: 'monday.com'
}, {
  name: 'Asana',
  domain: 'asana.com'
}, {
  name: 'Trello',
  domain: 'trello.com'
}, {
  name: 'Notion',
  domain: 'notion.so'
}, {
  name: 'Jira',
  domain: 'atlassian.com'
}, {
  name: 'Time Doctor',
  domain: 'timedoctor.com'
}, {
  name: 'Desklog',
  domain: 'desklog.io'
}];
const dartBenefits = ['AI-native project management', 'Unlimited users & workspaces', 'Built-in automation & workflows', 'Clean, lightning-fast interface', 'Custom views (Kanban, List, Calendar)', 'Seamless MR & PAM integration'];
const features = [{
  icon: <ListTodo size={22} data-id="element-1985" />,
  title: 'Task Creation',
  desc: 'Tell MR what needs to happen and tasks are created, formatted, and assigned — automatically. No more typing everything out.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Users size={22} data-id="element-1986" />,
  title: 'Smart Assignment',
  desc: 'MR knows who on your team handles what. Tasks go to the right person based on their role and workload.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Clock size={22} data-id="element-1987" />,
  title: 'Deadline Management',
  desc: 'Every task has a deadline. PAM watches them all and warns you before anything runs late.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <MessageSquare size={22} data-id="element-1988" />,
  title: 'Client Follow-Up',
  desc: "Waiting on a client for content, approval, or feedback? PAM reaches out with a friendly reminder so you don't have to nag.",
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Bell size={22} data-id="element-1989" />,
  title: 'Team Follow-Up',
  desc: "PAM checks in with your team on their tasks — did you finish this? Do you need help? — so you don't have to micromanage.",
  color: 'bg-teal/10 text-teal'
}, {
  icon: <AlertTriangle size={22} data-id="element-1990" />,
  title: 'Blocker Detection',
  desc: 'Something stuck? PAM spots it and escalates before it becomes a bigger problem.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <BarChart size={22} data-id="element-1991" />,
  title: 'Status Reports',
  desc: "Get automatic daily or weekly summaries of what got done, what's in progress, and what needs attention.",
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Inbox size={22} data-id="element-1992" />,
  title: 'Inbox Reminders',
  desc: 'PAM reminds team members to check their project inbox and respond to messages — building good habits automatically.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Bot size={22} data-id="element-1993" />,
  title: 'AI Agent Execution',
  desc: 'Need content written, a page built, or data organized? MR triggers specialized AI agents to actually DO the work — not just track it.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Workflow size={22} data-id="element-1994" />,
  title: 'Progress Tracking',
  desc: 'See exactly where every project stands at a glance. No more digging through tools to find answers.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <PieChart size={22} data-id="element-1995" />,
  title: 'Budget & Time Tracking',
  desc: 'Keep projects from going over budget. MR tracks time and costs so there are no surprises.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Repeat size={22} data-id="element-1996" />,
  title: 'Recurring Templates',
  desc: 'Do the same type of project often? Set it up once and MR reuses the template every time — same quality, zero setup.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Eye size={22} data-id="element-1997" />,
  title: 'Client Visibility',
  desc: 'Give clients a window into progress without them bugging you for updates. They see what they need to see.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <ShieldCheck size={22} data-id="element-1998" />,
  title: 'Quality Checks',
  desc: 'Before marking anything complete, PAM verifies the work meets your standards. No more accepting half-done tasks.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Network size={22} data-id="element-1999" />,
  title: 'Cross-Team Coordination',
  desc: 'When multiple teams or departments are involved, PAM keeps everyone aligned and moving in the same direction.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <CheckSquare size={22} data-id="element-2000" />,
  title: 'Habit Building',
  desc: 'PAM gently trains your team to update tasks, check inboxes, and communicate — building the habits that make projects run smoothly.',
  color: 'bg-purple/10 text-purple'
}];
const withoutMr = ['Tasks disappear and deadlines are missed', 'Clients are forgotten and projects stall', 'You spend hours chasing people for updates', 'You are the bottleneck for every decision', 'Constant stress and late-night worrying', 'Paying a project manager who needs managing'];
const withMr = ['Every task tracked and followed up on automatically', 'Clients gently reminded before things are late', 'Clean status reports delivered to you automatically', 'Projects move forward without your constant input', 'Peace of mind knowing nothing falls through the cracks', 'A flawless AI project manager that never sleeps'];
export function ProjectManager() {
  return <div className="w-full bg-white" data-id="element-2001">
      <Nav data-id="element-2002" />

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative bg-[#0F1D3A] pt-44 pb-24 px-6 overflow-hidden" data-id="element-2003">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple/15 rounded-full blur-[120px] pointer-events-none" data-id="element-2004" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal/10 rounded-full blur-[100px] pointer-events-none" data-id="element-2005" />
        <div className="max-w-5xl mx-auto relative z-10 text-center" data-id="element-2006">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-2007">
            <motion.span variants={fadeUp} className="inline-block text-teal text-sm font-bold tracking-widest uppercase mb-4" data-id="element-2008">
              Self-Managing Projects
            </motion.span>
            <motion.h1 variants={fadeUp} className="font-poppins font-extrabold text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6" data-id="element-2009">
              Your Projects. <br className="hidden md:block" data-id="element-2010" />
              <span className="text-gradient" data-id="element-2011">Managing Themselves.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-2012">
              No more chasing your team, wondering if tasks got done, or staying
              up at night worrying about deadlines. MR and PAM handle every
              detail — so you don't have to.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-id="element-2013">
              <Link to="/contact" className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow" data-id="element-2014">
                Talk to MR
              </Link>
              <Link to="/how-it-works" className="btn-ghost hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] transition-shadow" data-id="element-2015">
                See How It Works
              </Link>
            </motion.div>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-6" data-id="element-2016">
              {[{
              value: '100%',
              label: 'Every Task Tracked'
            }, {
              value: '100%',
              label: 'Every Deadline Met'
            }, {
              value: '100%',
              label: 'Every Person Accountable'
            }].map(stat => <div key={stat.label} className="bg-white/5 border border-white/10 rounded-xl px-6 py-3 text-center" data-id="element-2017">
                  <div className="text-2xl font-bold text-white" data-id="element-2018">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-400 font-medium" data-id="element-2019">
                    {stat.label}
                  </div>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ PAIN SECTION ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2020">
        <div className="max-w-6xl mx-auto" data-id="element-2021">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2022">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2023">
              Sound Familiar?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2024">
              If you're exhausted from constantly checking if things got done,
              you're not alone.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid md:grid-cols-2 gap-6" data-id="element-2025">
            {painPoints.map(p => <motion.div key={p.title} variants={fadeUp} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:border-purple/40 hover:shadow-md transition-all duration-300" data-id="element-2026">
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500 mb-4" data-id="element-2027">
                  {p.icon}
                </div>
                <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-2" data-id="element-2028">
                  {p.title}
                </h3>
                <p className="text-gray-500 leading-relaxed" data-id="element-2029">{p.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ YOUR MEETINGS ALREADY DO THE WORK ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2030">
        <div className="max-w-5xl mx-auto" data-id="element-2031">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={fadeUp} className="relative bg-gradient-to-br from-[#0F1D3A] to-[#1A447A] rounded-2xl p-10 md:p-14 overflow-hidden" data-id="element-2032">
            <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-purple/20 rounded-full blur-[100px] pointer-events-none" data-id="element-2033" />
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-10" data-id="element-2034">
              <div className="flex-1" data-id="element-2035">
                <div className="inline-flex items-center gap-2 bg-teal/20 text-teal px-4 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-2036">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2037">
                    <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" data-id="element-2038" />
                    <rect x="2" y="6" width="14" height="12" rx="2" data-id="element-2039" />
                  </svg>
                  Already Built Into Your Day
                </div>
                <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-6" data-id="element-2040">
                  Your Meetings Already Do The Work.
                </h2>
                <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-2041">
                  When you hop on a Zoom call or Google Meet, that recording
                  goes straight to MR's brain. Tasks, action items, and next
                  steps are created automatically — right from the conversation
                  you were already having.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-2042">
                  No repeating yourself. No sitting down after the call to type
                  everything out. No scheduling another meeting just to make
                  sure you captured it all.{' '}
                  <strong className="text-white" data-id="element-2043">
                    MR was listening the whole time.
                  </strong>
                </p>
                <p className="text-gray-400 text-base italic" data-id="element-2044">
                  This is how you get your time back — by leveraging the things
                  you're already doing.
                </p>
              </div>
              <div className="w-full md:w-1/3 flex flex-col gap-4" data-id="element-2045">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-2046">
                  <div className="text-xs text-teal font-bold mb-1" data-id="element-2047">
                    📹 Zoom Call Ends
                  </div>
                  <div className="text-sm text-gray-300" data-id="element-2048">
                    Recording sent to MR's brain
                  </div>
                </div>
                <div className="flex justify-center" data-id="element-2049">
                  <ArrowRight size={20} className="text-purple rotate-90" data-id="element-2050" />
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-2051">
                  <div className="text-xs text-purple font-bold mb-1" data-id="element-2052">
                    🧠 MR Processes
                  </div>
                  <div className="text-sm text-gray-300" data-id="element-2053">
                    Extracts tasks, deadlines & action items
                  </div>
                </div>
                <div className="flex justify-center" data-id="element-2054">
                  <ArrowRight size={20} className="text-teal rotate-90" data-id="element-2055" />
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-2056">
                  <div className="text-xs text-teal font-bold mb-1" data-id="element-2057">
                    ✅ Tasks Created
                  </div>
                  <div className="text-sm text-gray-300" data-id="element-2058">
                    Assigned, scheduled & tracked by PAM
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ MEET PAM ═══════════════ */}
      <section className="py-24 px-6 bg-white" data-id="element-2059">
        <div className="max-w-5xl mx-auto" data-id="element-2060">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={fadeUp} className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-2061">
            <div className="absolute inset-0 bg-gradient-to-br from-purple/5 via-white to-teal/5" data-id="element-2062" />
            <div className="absolute inset-0 border-2 border-transparent bg-gradient-to-br from-purple to-teal opacity-20 rounded-3xl" style={{
            maskImage: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            maskComposite: 'exclude'
          }} data-id="element-2063" />

            <div className="relative z-10 p-10 md:p-16 flex flex-col md:flex-row items-center gap-12" data-id="element-2064">
              <div className="flex-1 text-center md:text-left" data-id="element-2065">
                <div className="inline-flex items-center gap-2 bg-purple/10 text-purple px-4 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-2066">
                  <Sparkles size={16} data-id="element-2067" /> Meet Your New Project Manager
                </div>
                <h2 className="font-poppins font-bold text-4xl md:text-5xl text-gray-900 mb-6" data-id="element-2068">
                  Say Hello to <span className="text-gradient" data-id="element-2069">PAM.</span>
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed mb-6" data-id="element-2070">
                  PAM (Project Account Manager) is an AI agent that lives inside
                  your project management system. She works alongside MR to make
                  sure absolutely nothing falls through the cracks.
                </p>
                <p className="text-gray-600 text-lg leading-relaxed font-medium" data-id="element-2071">
                  She's the project manager you always wished you had — one that
                  never forgets, never drops the ball, and never needs to be
                  managed.
                </p>
              </div>
              <div className="w-full md:w-1/3 flex flex-col gap-4" data-id="element-2072">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-purple/10 flex items-start gap-3" data-id="element-2073">
                  <CheckCircle2 className="text-purple shrink-0 mt-0.5" size={20} data-id="element-2074" />
                  <span className="text-sm text-gray-700 font-medium" data-id="element-2075">
                    Stays on top of every task
                  </span>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-teal/10 flex items-start gap-3" data-id="element-2076">
                  <CheckCircle2 className="text-teal shrink-0 mt-0.5" size={20} data-id="element-2077" />
                  <span className="text-sm text-gray-700 font-medium" data-id="element-2078">
                    Follows up with team members
                  </span>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-purple/10 flex items-start gap-3" data-id="element-2079">
                  <CheckCircle2 className="text-purple shrink-0 mt-0.5" size={20} data-id="element-2080" />
                  <span className="text-sm text-gray-700 font-medium" data-id="element-2081">
                    Reminds clients about deadlines
                  </span>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-teal/10 flex items-start gap-3" data-id="element-2082">
                  <CheckCircle2 className="text-teal shrink-0 mt-0.5" size={20} data-id="element-2083" />
                  <span className="text-sm text-gray-700 font-medium" data-id="element-2084">
                    Escalates blockers instantly
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ HOW IT ACTUALLY WORKS ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2085">
        <div className="max-w-5xl mx-auto" data-id="element-2086">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-16" data-id="element-2087">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2088">
              How It Actually Works
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2089">
              Let's look at a real-world scenario. Here's what happens when you
              need to build a website.
            </motion.p>
          </motion.div>

          <div className="relative" data-id="element-2090">
            {/* Connecting Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple via-teal to-purple opacity-20 hidden md:block" data-id="element-2091" />

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-80px'
          }} variants={stagger} className="space-y-12" data-id="element-2092">
              {/* Step 1 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2093">
                <div className="md:w-1/2 flex justify-end" data-id="element-2094">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2095">
                    <div className="absolute top-6 -right-3 w-6 h-6 bg-white border-t border-r border-gray-100 rotate-45 hidden md:block" data-id="element-2096" />
                    <div className="text-sm font-bold text-purple mb-2" data-id="element-2097">
                      Step 1: You Talk
                    </div>
                    <p className="text-gray-600 text-sm" data-id="element-2098">
                      "Hey MR, we need to build the Johnson website by March
                      15th."
                    </p>
                  </div>
                </div>
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-purple text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2099">
                  1
                </div>
                <div className="md:w-1/2" data-id="element-2100" />
              </motion.div>

              {/* Step 2 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2101">
                <div className="md:w-1/2" data-id="element-2102" />
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-teal text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2103">
                  2
                </div>
                <div className="md:w-1/2 flex justify-start" data-id="element-2104">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2105">
                    <div className="absolute top-6 -left-3 w-6 h-6 bg-white border-b border-l border-gray-100 rotate-45 hidden md:block" data-id="element-2106" />
                    <div className="text-sm font-bold text-teal mb-2" data-id="element-2107">
                      Step 2: MR Creates
                    </div>
                    <p className="text-gray-600 text-sm" data-id="element-2108">
                      MR creates the full project — tasks, deadlines,
                      assignments — automatically.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Step 3 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2109">
                <div className="md:w-1/2 flex justify-end" data-id="element-2110">
                  <div className="bg-purple/5 p-6 rounded-xl shadow-sm border border-purple/20 w-full md:w-[400px] relative" data-id="element-2111">
                    <div className="absolute top-6 -right-3 w-6 h-6 bg-[#f9f5ff] border-t border-r border-purple/20 rotate-45 hidden md:block" data-id="element-2112" />
                    <div className="text-sm font-bold text-purple mb-2 flex items-center gap-2" data-id="element-2113">
                      <Sparkles size={14} data-id="element-2114" /> Step 3: PAM Follows Up
                    </div>
                    <p className="text-gray-700 text-sm italic" data-id="element-2115">
                      "Hey Stacey, just a heads up — the homepage content is due
                      Thursday. If it's not ready, I'll push the deadline back a
                      week so we stay on track."
                    </p>
                  </div>
                </div>
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-purple text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2116">
                  3
                </div>
                <div className="md:w-1/2" data-id="element-2117" />
              </motion.div>

              {/* Step 4 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2118">
                <div className="md:w-1/2" data-id="element-2119" />
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-teal text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2120">
                  4
                </div>
                <div className="md:w-1/2 flex justify-start" data-id="element-2121">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2122">
                    <div className="absolute top-6 -left-3 w-6 h-6 bg-white border-b border-l border-gray-100 rotate-45 hidden md:block" data-id="element-2123" />
                    <div className="text-sm font-bold text-teal mb-2" data-id="element-2124">
                      Step 4: PAM Verifies
                    </div>
                    <p className="text-gray-600 text-sm" data-id="element-2125">
                      Tasks get completed, PAM verifies quality, updates the
                      project, and keeps everyone in the loop.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Step 5 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2126">
                <div className="md:w-1/2 flex justify-end" data-id="element-2127">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2128">
                    <div className="absolute top-6 -right-3 w-6 h-6 bg-white border-t border-r border-gray-100 rotate-45 hidden md:block" data-id="element-2129" />
                    <div className="text-sm font-bold text-purple mb-2" data-id="element-2130">
                      Step 5: You Relax
                    </div>
                    <p className="text-gray-600 text-sm" data-id="element-2131">
                      You get a clean status report without asking for one. The
                      project is done.
                    </p>
                  </div>
                </div>
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-purple text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2132">
                  5
                </div>
                <div className="md:w-1/2" data-id="element-2133" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════ WORKS WITH ANY PM TOOL ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2134">
        <div className="max-w-5xl mx-auto" data-id="element-2135">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2136">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-teal/10 text-teal px-4 py-1.5 rounded-full text-sm font-semibold mb-4" data-id="element-2137">
              <Plug size={16} data-id="element-2138" /> Works With Any PM Tool
            </motion.div>
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2139">
              We Integrate With What You Already Love.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2140">
              Already using a project management tool? Keep it. MR and PAM plug
              right in and make it actually work the way it should.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid grid-cols-2 sm:grid-cols-4 gap-6" data-id="element-2141">
            {pmLogos.map(tool => <motion.div key={tool.name} variants={fadeUp} className="bg-gray-50 rounded-xl p-5 flex flex-col items-center gap-3 border border-gray-200 hover:border-purple/40 hover:shadow-md transition-all duration-300" data-id="element-2142">
                <img src={`https://logo.clearbit.com/${tool.domain}`} alt={tool.name} className="w-10 h-10 rounded-lg object-contain" onError={e => {
              const target = e.target as HTMLImageElement;
              target.src = `https://www.google.com/s2/favicons?domain=${tool.domain}&sz=128`;
            }} data-id="element-2143" />
                <span className="text-sm font-semibold text-gray-700" data-id="element-2144">
                  {tool.name}
                </span>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ OR GET DART AI INCLUDED ═══════════════ */}
      <section className="py-20 px-6 bg-[#0F1D3A]" data-id="element-2145">
        <div className="max-w-5xl mx-auto" data-id="element-2146">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-2147">
            <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-2148">
              <span className="inline-flex items-center gap-2 bg-purple/10 text-purple px-4 py-1.5 rounded-full text-sm font-semibold mb-4" data-id="element-2149">
                <Gift size={16} data-id="element-2150" /> Included With Your Complete Package
              </span>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4" data-id="element-2151">
                Or Get Your Own Workspace —{' '}
                <span className="text-gradient" data-id="element-2152">On Us.</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-2" data-id="element-2153">
                Don't have a PM tool yet? When you go with our complete package,
                you get your own project workspace inside{' '}
                <strong className="text-white" data-id="element-2154">Dart AI</strong> — an AI-native
                project management system included at no extra cost.
              </p>
              <p className="text-gray-500 text-sm" data-id="element-2155">
                Already have a PM tool? That's totally fine — this is a bonus,
                not a requirement.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-10" data-id="element-2156">
              <div className="flex flex-col md:flex-row items-center gap-6 mb-8" data-id="element-2157">
                <div className="flex items-center gap-3" data-id="element-2158">
                  <div className="w-12 h-12 rounded-xl bg-purple/20 flex items-center justify-center" data-id="element-2159">
                    <Layout size={24} className="text-purple" data-id="element-2160" />
                  </div>
                  <div data-id="element-2161">
                    <h3 className="text-xl font-poppins font-bold text-white" data-id="element-2162">
                      Project Workspace
                    </h3>
                    <span className="text-xs text-gray-400 bg-white/5 px-2 py-0.5 rounded-full" data-id="element-2163">
                      Powered by Dart AI
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2164">
                {dartBenefits.map(b => <div key={b} className="flex items-start gap-3" data-id="element-2165">
                    <CheckCircle2 size={18} className="text-teal shrink-0 mt-0.5" data-id="element-2166" />
                    <span className="text-gray-300 text-sm" data-id="element-2167">{b}</span>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ EVERYTHING MR & PAM HANDLE ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2168">
        <div className="max-w-6xl mx-auto" data-id="element-2169">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2170">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2171">
              Everything MR & PAM Handle
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2172">
              From creating tasks to gently nagging your clients for content —
              they do it all.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-2173">
            {features.map(f => <motion.div key={f.title} variants={fadeUp} className="bg-gray-50 rounded-xl p-5 border border-teal/40 hover:border-purple hover:shadow-md transition-all duration-300 group" data-id="element-2174">
                <div className={`w-10 h-10 rounded-xl ${f.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-2175">
                  {f.icon}
                </div>
                <h3 className="font-poppins font-semibold text-gray-900 mb-2" data-id="element-2176">
                  {f.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-2177">
                  {f.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ BEFORE & AFTER ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2178">
        <div className="max-w-5xl mx-auto" data-id="element-2179">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2180">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2181">
              The Difference Is Night and Day
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid md:grid-cols-2 gap-8" data-id="element-2182">
            {/* Without MR */}
            <motion.div variants={fadeUp} className="bg-red-50 border border-red-200 rounded-2xl p-8" data-id="element-2183">
              <h3 className="font-poppins font-bold text-xl text-red-700 mb-6 flex items-center gap-2" data-id="element-2184">
                <XCircle size={24} data-id="element-2185" /> Without MR & PAM
              </h3>
              <ul className="space-y-4" data-id="element-2186">
                {withoutMr.map(item => <li key={item} className="flex items-start gap-3 text-red-700/80" data-id="element-2187">
                    <XCircle size={16} className="shrink-0 mt-1 text-red-400" data-id="element-2188" />
                    <span className="text-sm leading-relaxed" data-id="element-2189">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
            {/* With MR */}
            <motion.div variants={fadeUp} className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8" data-id="element-2190">
              <h3 className="font-poppins font-bold text-xl text-emerald-700 mb-6 flex items-center gap-2" data-id="element-2191">
                <CheckCircle2 size={24} data-id="element-2192" /> With MR & PAM
              </h3>
              <ul className="space-y-4" data-id="element-2193">
                {withMr.map(item => <li key={item} className="flex items-start gap-3 text-emerald-700/80" data-id="element-2194">
                    <CheckCircle2 size={16} className="shrink-0 mt-1 text-emerald-500" data-id="element-2195" />
                    <span className="text-sm leading-relaxed" data-id="element-2196">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ GET YOUR TIME BACK ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2197">
        <div className="max-w-5xl mx-auto text-center" data-id="element-2198">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-2199">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-2200">
              What If You Could Get Your Time Back?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-2201">
              Think about all the hours you spend chasing updates, reminding
              people, checking if things got done, and worrying about what fell
              through the cracks. Now imagine all of that handled —
              automatically, consistently, every single day.
            </motion.p>
            <motion.div variants={fadeUp} className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto mb-10" data-id="element-2202">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-2203">
                <div className="text-4xl font-bold text-purple mb-2" data-id="element-2204">10+</div>
                <div className="text-sm text-gray-600" data-id="element-2205">
                  Hours saved per week on follow-ups and status checks
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-2206">
                <div className="text-4xl font-bold text-teal mb-2" data-id="element-2207">0</div>
                <div className="text-sm text-gray-600" data-id="element-2208">
                  Tasks that fall through the cracks
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-2209">
                <div className="text-4xl font-bold text-purple mb-2" data-id="element-2210">100%</div>
                <div className="text-sm text-gray-600" data-id="element-2211">
                  Of your focus back on growing your business
                </div>
              </div>
            </motion.div>
            <motion.p variants={fadeUp} className="text-gray-500 text-base max-w-2xl mx-auto" data-id="element-2212">
              MR and PAM aren't here to replace anyone — they're here to handle
              the tedious, time-consuming details that eat up your day. The
              stuff that keeps you up at night. So you can focus on the work
              that actually moves the needle.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ FINAL CTA ═══════════════ */}
      <section className="py-24 px-6 bg-[#0F1D3A] text-center" data-id="element-2213">
        <div className="max-w-3xl mx-auto" data-id="element-2214">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-2215">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-white mb-6" data-id="element-2216">
              Ready to Stop Being Everyone's Project Manager?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-400 text-lg mb-10 max-w-xl mx-auto" data-id="element-2217">
              Let MR and PAM handle the details, the follow-ups, and the
              deadlines — so you can focus on growing your business.
            </motion.p>
            <motion.div variants={fadeUp} data-id="element-2218">
              <Link to="/contact" className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow text-lg px-10 py-4" data-id="element-2219">
                Talk to MR
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-2220" />
    </div>;
}